<script>
import * as Sentry from '@sentry/browser';
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from 'luxon';
import { formatMoney } from '~/javascripts/lib/format_money';
import { sprintf, __ } from '~/javascripts/lib/locale';
import { MIN_VALIDATION_MESSAGE, MIN_SEATS_REASON } from '~/javascripts/constants';
import {
  PROMO_CODE_QUANTITY_ERROR_MESSAGE,
  INVALID_PROMO_CODE_ERROR_MESSAGE,
} from '~/javascripts/subscription/constants';
import TransactionCard from '~/javascripts/purchase/components/transaction_card.vue';
import PurchaseSubscriptionDetails from '~/javascripts/purchase/components/purchase_subscription_details.vue';
import PurchaseConfirmationCard from '~/javascripts/purchase/components/purchase_confirmation_card_deprecated.vue';
import PaymentMethodsSelect from '~/javascripts/purchase/shared/components/payment_methods_select.vue';
import TransactionLineItem from '~/javascripts/purchase/components/transaction_line_item.vue';
import GroupDetails from '~/javascripts/subscription/components/subscription/group_details.vue';
import PromoCodeInput from '~/javascripts/subscription/components/promo_code_input.vue';
import { purchaseSubscription } from '~/javascripts/api';
import { newSubscriptionPayload } from '~/javascripts/subscription_payloads';
import { toLocaleDateString } from '~/javascripts/lib/datetime';
import Tracking from '~/javascripts/tracking';
import { TRACKING_ACTIONS, TRACKING_CATEGORIES } from '~/javascripts/tracking/constants';
import BillingAccountAddressForm from '~/javascripts/billing_account/components/billing_account_address_form.vue';
import CompanyDetailsForm from '~/javascripts/billing_account/components/company_details_form.vue';
import { getCompanyDetails } from '~/javascripts/billing_account/utils';
import { trackTransaction } from '~/javascripts/google_tag_manager';
import {
  isInvalidPromoCodeError,
  getChargeItem,
  getDiscountItem,
} from '~/javascripts/subscription/utils';
import BillingAccountDetails from '../../billing_account/components/billing_account_details.vue';

export default {
  name: 'NewGitlabSubscriptionForm',
  components: {
    PromoCodeInput,
    BillingAccountAddressForm,
    PurchaseSubscriptionDetails,
    PurchaseConfirmationCard,
    PaymentMethodsSelect,
    GroupDetails,
    TransactionCard,
    TransactionLineItem,
    CompanyDetailsForm,
    BillingAccountDetails,
  },
  inject: ['eligibleToUsePromoCode', 'planCode', 'customerBillingAccountsPresent', 'promoCode'],
  props: {
    namespaces: {
      type: Array,
      required: true,
    },
    planId: {
      type: String,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    currentNamespaceId: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isComplete: false,
      selectedPaymentMethodId: null,
      selectedGroup: null,
      invoice: undefined,
      serviceStartDate: undefined,
      serviceEndDate: undefined,
      invoiceItems: [],
      isLoading: false,
      quantity: 1,
      isInvalidQuantity: false,
      idempotencyKeys: {},
      addressForm: {
        address1: this.user.address1,
        address2: this.user.address2,
        country: this.user.country,
        state: this.user.state,
        city: this.user.city,
        zipCode: this.user.zipCode,
      },
      companyDetailsForm: {
        companyName: this.user.companyName,
        // Should be refactored to using `tax`-names instead of `vat`:
        // https://gitlab.com/gitlab-org/customers-gitlab-com/-/issues/7469
        taxId: this.user.vatCode,
      },
      addressFormValid: Boolean(this.customerBillingAccountsPresent),
      companyDetailsFormValid: Boolean(this.customerBillingAccountsPresent),
      promoCode: this.promoCode, // Initialize from URL parameter
      applyingPromoCode: false,
      promoCodeErrorMessage: undefined,
    };
  },
  computed: {
    total() {
      return this.invoice?.amount ?? 0;
    },
    subTotal() {
      // return this.chargeItem?.charge_amount ?? 0; override jh
      return this.invoice?.amount_without_tax ?? 0;
    },
    discount() {
      return this.discountItem?.charge_amount ?? 0;
    },
    unitPrice() {
      return this.chargeItem?.unit_price ?? 0;
    },
    tax() {
      return this.invoice?.tax_amount ?? 0;
    },
    minUserCount() {
      return this.selectedGroup?.userCount ?? 1;
    },
    purchaseButtonDisabled() {
      return (
        !this.invoice ||
        this.isLoading ||
        this.isComplete ||
        !this.selectedPaymentMethodId ||
        !this.selectedGroup ||
        this.isInvalidQuantity ||
        !this.addressFormValid ||
        !this.companyDetailsFormValid
      );
    },
    subscriptionDetailsTitle() {
      if (!this.serviceStartDate || !this.serviceEndDate) {
        return '';
      }
      return `${__('New subscription for')} ${this.formatDate(
        this.serviceStartDate,
      )} - ${this.formatDate(this.serviceEndDate)}`;
    },
    minQuantityValidationMsg() {
      const minValidationMessage = sprintf(MIN_VALIDATION_MESSAGE, {
        minValue: this.minUserCount,
      });
      // eslint-disable-next-line sonarjs/no-nested-template-literals
      return `${minValidationMessage}${this.minUserCount > 1 ? ` (${MIN_SEATS_REASON}).` : '.'}`;
    },
    idempotencyKeyParams() {
      return {
        namespaceId: this.selectedGroupId,
        planId: this.planId,
        quantity: this.quantity,
        selectedPaymentMethodId: this.selectedPaymentMethodId,
      };
    },
    serializedIdempotencyKeyParams() {
      return JSON.stringify(this.idempotencyKeyParams);
    },
    trackingLabel() {
      return `buy_${this.planCode}_saas`;
    },
    groups() {
      return (
        // Return the single pre-selected namespace
        this.namespaces ?? []
      );
    },
    selectedGroupId() {
      return this.selectedGroup?.id;
    },
    chargeItem() {
      return getChargeItem(this.invoiceItems);
    },
    discountItem() {
      return getDiscountItem(this.invoiceItems);
    },
    hasDiscount() {
      return Boolean(this.discountItem);
    },
    hasValidPriceDetails() {
      return Boolean(this.invoice) && !this.isInvalidQuantity;
    },
    showPromoCodeSuccessAlert() {
      return this.hasValidPriceDetails && this.hasDiscount;
    },
    formattedDiscount() {
      return formatMoney(this.discount, !this.hasValidPriceDetails);
    },
    namespaceDetails() {
      return {
        id: this.selectedGroupId,
        webUrl: this.selectedGroup.webUrl || null,
      };
    },
    namespaceName() {
      return this.selectedGroup?.name || '';
    },
  },
  watch: {
    idempotencyKeyParams: {
      handler() {
        this.updateIdempotencyKey();
      },
      deep: true,
    },
  },
  created() {
    this.tracking = new Tracking();
    this.initializeSelectedGroup();
    this.updateIdempotencyKey();
  },
  mounted() {
    // Auto-apply promo code if present in URL
    if (this.promoCode && this.eligibleToUsePromoCode) {
      this.applyPromoCode(this.promoCode);
    }
  },
  methods: {
    formatMoney,
    handleInvalidQuantity() {
      this.isInvalidQuantity = true;
    },
    paymentMethodClick(paymentMethodId) {
      this.trackEvent(TRACKING_ACTIONS.selectPaymentMethod);
      this.selectedPaymentMethodId = paymentMethodId;
    },
    clearError() {
      this.promoCodeErrorMessage = '';
      this.$emit('error', null);
    },
    getPaymentMethodIdParam({ isConfirmed }) {
      if (!isConfirmed) {
        return undefined;
      }
      return this.selectedPaymentMethodId;
    },
    getRequestParams({ isConfirmed }) {
      const sendCustomerInformation = !this.customerBillingAccountsPresent && isConfirmed;
      return newSubscriptionPayload({
        planId: this.planId,
        seats: this.quantity,
        isConfirmed,
        namespaceId: this.selectedGroupId,
        namespaceName: this.namespaceName,
        paymentMethodId: this.getPaymentMethodIdParam({ isConfirmed }),
        idempotencyKey: this.idempotencyKeys[this.serializedIdempotencyKeyParams],
        addressDetails: sendCustomerInformation ? this.addressForm : undefined,
        companyDetails: sendCustomerInformation
          ? getCompanyDetails(this.companyDetailsForm, this.addressForm.country)
          : undefined,
        promoCode: this.promoCode,
      });
    },
    handleError(error) {
      if (isInvalidPromoCodeError(error)) {
        this.promoCodeErrorMessage = INVALID_PROMO_CODE_ERROR_MESSAGE;
        this.promoCode = undefined;
        this.trackEvent(TRACKING_ACTIONS.promoCodeFailureResponse);
      } else {
        this.$emit('error', error);
        this.reportError(error);
      }
    },
    reportError(error) {
      Sentry.captureException(error, {
        tags: {
          vue_component: this.$options.name,
        },
      });
    },
    handleQuantityChange(userQuantity) {
      this.trackEvent(TRACKING_ACTIONS.updateSeatCount);
      this.isInvalidQuantity = false;

      this.quantity = userQuantity;
      this.previewTransaction();
    },
    previewTransaction() {
      this.isLoading = true;
      const requestParams = this.getRequestParams({ isConfirmed: false });

      this.clearError();

      purchaseSubscription(requestParams)
        .then((response) => {
          const { invoice_item: invoiceItems, invoice } = response;
          this.invoice = invoice;
          this.invoiceItems = invoiceItems;
          this.serviceStartDate = this.chargeItem?.service_start_date;
          this.serviceEndDate = this.chargeItem?.service_end_date;

          if (this.hasDiscount && this.applyingPromoCode) {
            this.trackEvent(TRACKING_ACTIONS.promoCodeSuccessResponse);
          }
        })
        .catch((error) => {
          this.resetInvoiceDetails();
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.applyingPromoCode = false;
        });
    },
    makePayment() {
      this.isLoading = true;
      this.clearError();

      if (this.currentNamespaceId !== this.selectedGroupId) {
        this.trackEvent(TRACKING_ACTIONS.updateGroup);
      }

      const requestParams = this.getRequestParams({ isConfirmed: true });

      return purchaseSubscription(requestParams)
        .then(this.paymentSuccess)
        .catch(this.handlePurchaseError)
        .finally(() => {
          this.isLoading = false;
        });
    },
    paymentSuccess(response) {
      if (response.success !== true) {
        throw response.errors;
      }

      this.isComplete = true;

      this.trackTransaction(response.payment_method_id);

      this.$emit('success', response.pay_url);
    },
    trackTransaction(paymentMethodId) {
      const revenue = this.invoice.amount_without_tax ?? 0;
      const transactionDetails = {
        paymentOption: paymentMethodId,
        revenue,
        tax: this.tax,
        price: this.unitPrice,
        planId: this.planId,
        quantity: this.quantity,
        namespaceId: this.selectedGroupId,
      };

      trackTransaction(transactionDetails);
    },
    resetInvoiceDetails() {
      this.invoice = null;
      this.invoiceItems = [];
    },
    formatDate(invoiceServiceDate) {
      return toLocaleDateString(invoiceServiceDate, DateTime.DATE_FULL);
    },
    initializeSelectedGroup() {
      const [selectedGroup] = this.groups || [];

      if (selectedGroup) {
        this.handleGroupSelect(selectedGroup);
      } else {
        this.previewTransaction();
      }
    },
    handleGroupSelect(group) {
      this.quantity = group?.userCount ?? 1;

      this.selectedGroup = group;
      this.previewTransaction();
    },
    handlePurchaseError(error = {}) {
      const { status } = error;
      // Regenerate the idempotency key on client-side error responses (4xx) to ensure the server regards the new request.
      // Context: https://gitlab.com/gitlab-org/gitlab/-/merge_requests/129830#note_1522796835.
      if (status && status >= 400 && status < 500) {
        this.regenerateIdempotencyKey();
      }

      this.handleError(error);
    },
    updateIdempotencyKey() {
      this.idempotencyKeys[this.serializedIdempotencyKeyParams] =
        this.idempotencyKeys[this.serializedIdempotencyKeyParams] ?? uuidv4();
    },
    regenerateIdempotencyKey() {
      this.idempotencyKeys[this.serializedIdempotencyKeyParams] = uuidv4();
    },
    handleAcceptTermsChange() {
      this.trackEvent(TRACKING_ACTIONS.clickTermsCheckbox);
    },
    trackEvent(action) {
      this.tracking.event(TRACKING_CATEGORIES.webstore, action, { label: this.trackingLabel });
    },
    handleAddressFormChange(data, isValid) {
      this.addressForm = data;
      this.addressFormValid = isValid;
    },
    handleCompanyFormChange(data, isValid) {
      this.companyDetailsForm = data;
      this.companyDetailsFormValid = isValid;
    },
    applyPromoCode(promoCode) {
      if (this.isInvalidQuantity) {
        this.promoCodeErrorMessage = PROMO_CODE_QUANTITY_ERROR_MESSAGE;
      } else {
        this.promoCode = promoCode;
        this.applyingPromoCode = true;
        this.previewTransaction();
        this.trackEvent(TRACKING_ACTIONS.clickApplyPromoCodeButton);
      }
    },
  },
};
</script>

<template>
  <div class="row gl-mb-3 gl-justify-between">
    <transaction-card :title="__('Subscription details')" container-classes="col-md-12 col-lg-8">
      <purchase-subscription-details
        :title="subscriptionDetailsTitle"
        :is-loading="isLoading"
        :purchase-quantity="quantity"
        :purchase-quantity-label="__('Seats')"
        :initial-min-quantity-count="minUserCount"
        :min-quantity-validation-msg="minQuantityValidationMsg"
        :unit-price="unitPrice"
        :unit-description="__('per user annually')"
        :sub-total="subTotal"
        show-overage-alert
        :sub-total-label="__('Subtotal for the new subscription')"
        @quantity-change="handleQuantityChange"
        @on-invalid-quantity="handleInvalidQuantity"
      >
        <template #description>
          <div v-if="selectedGroup">
            <div class="gl-mb-2 gl-font-bold">{{ __('Group') }}</div>
            <p class="gl-mb-2">{{ __('Your subscription will be applied to this group') }}</p>
            <group-details :namespace="namespaceName" :namespace-details="namespaceDetails" />
          </div>
        </template>

        <template v-if="eligibleToUsePromoCode" #promo-code>
          <promo-code-input
            :initial-promo-code="promoCode"
            :show-success-alert="showPromoCodeSuccessAlert"
            :is-parent-form-loading="isLoading"
            :is-applying-promo-code="applyingPromoCode"
            :error-message="promoCodeErrorMessage"
            @promo-code-submit="applyPromoCode"
          />
        </template>
      </purchase-subscription-details>

      <div class="gl-mb-5 gl-border-t-1 gl-border-gray-100 gl-pt-5 gl-border-t-solid">
        <billing-account-details v-if="customerBillingAccountsPresent" />
        <template v-else>
          <billing-account-address-form
            :initial-form-data="addressForm"
            :show-required-labels="true"
            @change="handleAddressFormChange"
          />
          <company-details-form
            :initial-form-data="companyDetailsForm"
            :show-required-labels="true"
            :selected-country="addressForm.country"
            @change="handleCompanyFormChange"
          />
        </template>
      </div>

      <transaction-line-item
        :is-loading="isLoading"
        :label="__('Subtotal')"
        :value="formatMoney(subTotal)"
        data-testid="transaction-subtotal"
      />

      <transaction-line-item
        v-if="hasDiscount"
        :is-loading="isLoading"
        label="Discount"
        :value="formattedDiscount"
        :show-as-header="false"
        :top-border-enabled="false"
        data-testid="transaction-discount"
      />

      <transaction-line-item
        :is-loading="isLoading"
        :label="__('Estimated taxes')"
        :value="formatMoney(tax)"
        data-testid="transaction-tax"
      />
    </transaction-card>

    <purchase-confirmation-card
      :card-title="__('Billing information')"
      :confirm-text="__('Buy subscription')"
      :disabled="purchaseButtonDisabled"
      :loading="isLoading"
      :total="total"
      @acceptTermsChange="handleAcceptTermsChange"
      @confirmPurchase="makePayment"
      @paymentMethodClick="paymentMethodClick"
    >
      <template #payment-form>
        <payment-methods-select @click="paymentMethodClick" />
      </template>
    </purchase-confirmation-card>
  </div>
</template>
